import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { Order_Bool_Exp } from '@/generated/graphql/graphql';

dayjs.extend(utc);

export const buildOrdersWhereForStatusCounts = (
  departmentId: number,
  filters: Record<string, unknown>,
  offset: number
): Order_Bool_Exp =>
  buildWhereClause(
    departmentId,
    { ...filters, status: 'ALL' },
    offset
  ) as unknown as Order_Bool_Exp;

const addStatusFilter = (where: Record<string, unknown>, filters: Record<string, unknown>) => {
  if (filters.status !== 'ALL') {
    where.statusName = { _eq: filters.status };
  }
};

const addSearchFilter = (where: Record<string, unknown>, searchText: string) => {
  if (searchText) {
    const search = `%${searchText}%`;
    where._or = [
      {
        client: {
          _or: [
            { phone: { _like: search } },
            { fullname: { _ilike: search } },
            { address: { district: { _ilike: search } } },
          ],
        },
      },
      { shortCode: { _ilike: search } },
      { positions: { product: { name: { _ilike: search } } } },
    ];
  }
};

const addProductsFilter = (where: Record<string, unknown>, products: unknown) => {
  if (products && Array.isArray(products) && products.length > 0) {
    where.positions = {
      productID: { _in: products },
    };
  }
};

const addBooleanFilters = (where: Record<string, unknown>, filters: Record<string, unknown>) => {
  if (filters.resale != null) {
    where.resale = { _eq: filters.resale };
  }

  if (filters.isSaved != null) {
    where.isSaved = { _eq: filters.isSaved };
  }

  if (filters.showOnlyExpress === true) {
    where.delivery_provider = {
      short_name: { _eq: 'DTK_EXPRESS' },
    };
  }
};

const addDriverFilter = (where: Record<string, unknown>, driverIDs: unknown) => {
  if (driverIDs && Array.isArray(driverIDs) && driverIDs.length > 0) {
    where.driverID = { _in: driverIDs };
  }
};

const addPriceRangeFilter = (where: Record<string, unknown>, priceRange: unknown) => {
  if (priceRange && Array.isArray(priceRange) && priceRange.length === 2) {
    const [minStr, maxStr] = priceRange;
    const priceConditions: Record<string, unknown> = {};

    if (minStr && minStr !== '' && !Number.isNaN(Number(minStr))) {
      priceConditions._gte = Number(minStr);
    }
    if (maxStr && maxStr !== '' && !Number.isNaN(Number(maxStr))) {
      priceConditions._lte = Number(maxStr);
    }

    if (Object.keys(priceConditions).length > 0) {
      where.totalCents = priceConditions;
    }
  }
};

const addDateFilters = (
  where: Record<string, unknown>,
  dates: Record<string, unknown> | undefined,
  offset: number
) => {
  if (!dates) return;

  const dateConditions: Record<string, unknown>[] = [];
  const dateFields = ['plannedDate', 'deliveredDate', 'updatedAt', 'createdAt'] as const;

  dateFields.forEach((dateField) => {
    const dateRange = dates[dateField];
    if (Array.isArray(dateRange) && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      const start = dayjs(dateRange[0]).utc().startOf('day').subtract(offset, 'hour').toISOString();
      const end = dayjs(dateRange[1]).utc().endOf('day').subtract(offset, 'hour').toISOString();

      dateConditions.push({ [dateField]: { _gte: start } }, { [dateField]: { _lte: end } });
    }
  });

  if (dateConditions.length > 0) {
    if (!where._and) where._and = [];
    where._and = (where._and as unknown[]).concat(dateConditions);
  }
};

export const buildWhereClause = (
  departmentId: number,
  filters: Record<string, unknown>,
  offset: number
) => {
  const where: Record<string, unknown> = {
    departmentId: { _eq: departmentId },
  };

  addStatusFilter(where, filters);
  addSearchFilter(where, filters.searchText as string);
  addProductsFilter(where, filters.products);
  addBooleanFilters(where, filters);
  addDriverFilter(where, filters.driverIDs);
  addPriceRangeFilter(where, filters.priceRange);
  addDateFilters(where, filters.dates as Record<string, unknown> | undefined, offset);

  return where;
};
